import { timeElapsed } from '@playbooks/utils/dates';
import { logger } from '@playbooks/utils/logger';

const AGENT_STREAM_CHUNKS = new Set([
	'text-start',
	'text-delta',
	'text-end',
	'reasoning-start',
	'reasoning-delta',
	'reasoning-end',
	'tool-input-start',
	'tool-input-delta',
	'tool-input-available',
	'tool-input-error',
	'tool-approval-request',
	'tool-output-available',
	'tool-output-error',
	'tool-output-denied',
	'source-url',
	'source-document',
	'file',
	'start-step',
	'finish-step',
	'start',
	'finish',
	'abort',
	'message-metadata',
	'error',
	'reasoning',
	'tool-call',
	'data-file-change',
	'data-status',
	'data-work-aggregate',
	'data-work-phase',
	'data-work-summary',
]);
const INTERNAL_AGENT_STREAM_CHUNKS = new Set(['data-agent-state']);
const AGENT_STREAM_UPDATE_MESSAGE = 'Unsupported agent stream chunks. Please update the playbooks agent.';
const AGENT_DATA_STREAM_CHUNKS = new Set([
	'data-file-change',
	'data-status',
	'data-work-aggregate',
	'data-work-phase',
	'data-work-summary',
]);
const AGENT_SNAPSHOT_STREAM_CHUNKS = new Set(['tool-call']);

const isTextPart = part => part?.type === 'text' && typeof part.text === 'string';

const getConversationMessageRawParts = (message: any) => {
	if (!message) return null;
	if (Array.isArray(message.parts)) return message.parts;
	if (message.parts && typeof message.parts === 'object') return message.parts;
	return null;
};

const getAgentPartText = value => {
	if (!value) return '';
	if (typeof value === 'string') return value;
	if (Array.isArray(value)) return value.map(getAgentPartText).filter(Boolean).join('\n');
	if (typeof value !== 'object') return '';

	if (typeof value.text === 'string') return value.text;
	if (value.parts) return getAgentPartText(value.parts);

	return '';
};

const normalizeConversationMessagePart = part => {
	if (!part) return null;
	if (typeof part === 'string') return { type: 'text', text: part };
	if (typeof part !== 'object') return null;

	if (part.type === 'text') {
		const text = getAgentPartText(part);
		return text ? { ...part, type: 'text', text } : null;
	}

	if (typeof part.type === 'string') return { ...part, type: part.type };

	const text = getAgentPartText(part);
	return text ? { type: 'text', text } : null;
};

const getAgentStreamChunkType = chunk => chunk?.type || chunk?.event;

const getAgentStreamChunkId = (chunk, messageId) => {
	return chunk?.id || chunk?.data?.id || `${getAgentStreamChunkType(chunk)}:${messageId}`;
};

const normalizeAgentDataStreamChunk = (chunk, type, messageId) => {
	const data = {
		...(chunk?.data || {}),
		...(chunk?.status ? { status: chunk.status } : {}),
		...(chunk?.position !== undefined ? { position: chunk.position } : {}),
		...(chunk?.runId ? { runId: chunk.runId } : {}),
		...(chunk?.messageId ? { messageId: chunk.messageId } : {}),
	};

	return {
		...chunk,
		type,
		id: getAgentStreamChunkId(chunk, messageId),
		data,
		transient: false,
	};
};

const normalizeAgentTextStreamChunk = (chunk, type, messageId, textPartValues) => {
	const id = chunk?.id || `text:${messageId}`;
	if (type === 'text-start') {
		textPartValues.set(id, '');
		return { ...chunk, type, id };
	}
	if (type === 'text-end') return { ...chunk, type, id };
	if (type !== 'text-delta') return { ...chunk, type, id };

	if (typeof chunk.delta === 'string') {
		const previousText = textPartValues.get(id) || '';
		textPartValues.set(id, `${previousText}${chunk.delta}`);
		return { ...chunk, type, id, delta: chunk.delta };
	}

	if (typeof chunk.text !== 'string') return { ...chunk, type, id, delta: '' };

	const previousText = textPartValues.get(id) || '';
	const delta = chunk.text.startsWith(previousText) ? chunk.text.slice(previousText.length) : chunk.text;
	textPartValues.set(id, chunk.text);
	return { ...chunk, type, id, delta };
};

const normalizeAgentReasoningStreamChunk = (chunk, messageId, reasoningPartValues) => {
	const id = chunk?.id || `reasoning:${messageId}`;
	const nextText = typeof chunk?.text === 'string' ? chunk.text : '';
	const previousText = reasoningPartValues.get(id) || '';
	const delta = nextText.startsWith(previousText) ? nextText.slice(previousText.length) : nextText;
	reasoningPartValues.set(id, nextText);
	if (!delta) return [];

	return [
		{ ...chunk, type: 'reasoning-start', id },
		{ ...chunk, type: 'reasoning-delta', id, delta },
		{ ...chunk, type: 'reasoning-end', id },
	];
};

const normalizeAgentStreamChunk = (chunk, messageId, textPartValues, reasoningPartValues) => {
	const type = getAgentStreamChunkType(chunk);
	if (!type) throw new Error('Agent stream chunk is missing a type.');
	if (INTERNAL_AGENT_STREAM_CHUNKS.has(type)) return [];
	if (!AGENT_STREAM_CHUNKS.has(type) && !String(type).startsWith('data-') && !String(type).startsWith('tool-')) {
		throw new Error(`${AGENT_STREAM_UPDATE_MESSAGE} ${type}.`);
	}
	if (AGENT_SNAPSHOT_STREAM_CHUNKS.has(type)) return [];
	if (type === 'reasoning') return normalizeAgentReasoningStreamChunk(chunk, messageId, reasoningPartValues);
	if (AGENT_DATA_STREAM_CHUNKS.has(type) || String(type).startsWith('data-')) {
		return [normalizeAgentDataStreamChunk(chunk, type, messageId)];
	}
	if (String(type).startsWith('text-')) {
		return [normalizeAgentTextStreamChunk(chunk, type, messageId, textPartValues)];
	}
	if (String(type).startsWith('reasoning-')) {
		const id = chunk?.id || `reasoning:${messageId}`;
		return [{ ...chunk, type, id }];
	}
	if (String(type).startsWith('tool-')) {
		return [{ ...chunk, type, toolCallId: chunk?.toolCallId || chunk?.id }];
	}
	return [{ ...chunk, type }];
};

export const normalizeConversationMessageParts = (parts: any, text = '') => {
	const values = Array.isArray(parts) ? parts : parts ? [parts] : [];
	const normalized = values.map(normalizeConversationMessagePart).filter(Boolean);
	if (normalized.length) return normalized;
	return text ? [{ type: 'text', text }] : [];
};

export const getConversationMessageText = (message: any) => {
	const parts = normalizeConversationMessageParts(getConversationMessageRawParts(message));
	const text = parts
		.filter(isTextPart)
		.map(part => part.text)
		.join('');
	return text || message?.text || '';
};

export const getConversationMessageParts = (message: any) => {
	return normalizeConversationMessageParts(getConversationMessageRawParts(message), message?.text || '');
};

export const getConversationMessageRuntimeId = (id: any) => {
	if (!id) return null;
	const value = String(id);
	return value.startsWith('conversation-message-') ? value : `conversation-message-${value}`;
};

export const getProjectOperatorRunRuntimeId = (id: any) => {
	if (!id) return null;
	const value = String(id);
	return value.startsWith('project-operator-run-') ? value : `project-operator-run-${value}`;
};

export const getAgentRuntimeMessageId = (id: any) => {
	if (!id) return null;
	const value = String(id);
	if (value.startsWith('project-operator-run-')) return getProjectOperatorRunRuntimeId(value);
	return getConversationMessageRuntimeId(value);
};

export const getConversationMessageServerId = (message: any) => {
	const value = message?.metadata?.agentServerMessageId || message?.metadata?.serverId || message?.id;
	if (!value) return null;
	return String(value).replace(/^conversation-message-/, '');
};

export const formatConversationMessage = (message: any) => {
	const text = message?.text || '';
	const parts = normalizeConversationMessageParts(getConversationMessageRawParts(message), text);
	const runtimeId = getAgentRuntimeMessageId(message.id);
	const serverId = getConversationMessageServerId(message);

	return {
		...message,
		id: runtimeId,
		metadata: {
			...(message.metadata || {}),
			conversationMessageId: runtimeId,
			agentServerMessageId: serverId,
		},
		parts,
	};
};

export const formatConversationMessages = (messages = []) => {
	return messages.map(formatConversationMessage);
};

export const normalizeAgentErrorText = error => {
	if (Array.isArray(error)) return normalizeAgentErrorText(error[0]);
	return error?.description || error?.message || error?.title || 'The agent request failed.';
};

export const formatAgentProjectName = text => {
	const value = String(text || '')
		.replace(/\s+/g, ' ')
		.trim();
	if (!value) return 'Untitled Project';
	return value.split(/[.!?]/)[0].slice(0, 60) || 'Untitled Project';
};

export const formatAgentProjectHref = (project, branch) => {
	const href = project?.appPath || project?.webPath || (project?.uuid ? `/projects/${project.uuid}` : '/projects');
	if (!branch?.name) return href;
	const separator = href.includes('?') ? '&' : '?';
	return `${href}${separator}branch=${encodeURIComponent(branch.name)}`;
};

export const AGENT_IMPLEMENT_PLAN_PROMPT = 'implement-plan';

export type AgentPromptOption = {
	id: string;
	label: string;
	description: string;
};

export type AgentPromptQuestion = {
	id: string;
	title: string;
	description: string;
	inputType: 'single-select' | 'multi-select';
	required: boolean;
	allowCustom: boolean;
	maxSelections: number;
	options: AgentPromptOption[];
};

export type AgentPrompt = {
	id: string;
	purpose?: string;
	batchKind?: 'standard' | 'scaffolding';
	questions: AgentPromptQuestion[];
};

export type AgentPromptAnswer = {
	promptId: string;
	purpose?: string | null;
	title: string;
	selectedOptions: Array<Pick<AgentPromptOption, 'id' | 'label'> & { value: string }>;
	feedback: string | null;
	skipped: boolean;
};

export type AgentPromptResponse = {
	type: 'data-agent-prompt-response';
	id: string;
	data: { responses: AgentPromptAnswer[] };
};

export const getAgentPromptData = prompt => prompt?.data || prompt || {};

export const getAgentPromptType = prompt => {
	const data = getAgentPromptData(prompt);
	return data.inputType || data.type || 'single-select';
};

export const getAgentPromptOptions = prompt => {
	const options = getAgentPromptData(prompt).options;
	return Array.isArray(options) ? options : [];
};

export const getAgentPrompts = prompt => {
	const data = getAgentPromptData(prompt);
	const prompts = data.questions;
	if (!Array.isArray(prompts) || !prompts.length) return [];
	return prompts.map(item => ({
		...item,
		data: { ...data, ...item },
	}));
};

export const isAgentMultiSelectPrompt = prompt => getAgentPromptType(prompt) === 'multi-select';

export const isImplementPlanPrompt = prompt => {
	const data = getAgentPromptData(prompt);
	return (
		data.purpose === AGENT_IMPLEMENT_PLAN_PROMPT ||
		data.action === AGENT_IMPLEMENT_PLAN_PROMPT ||
		/implement this plan/i.test(data.title || '')
	);
};

export const isAgentProjectConceptPrompt = prompt => getAgentPromptData(prompt).purpose === 'project-concepts';

export const normalizeAgentPromptOption = option => {
	if (!option || typeof option !== 'object' || Array.isArray(option)) return null;
	return {
		...option,
		id: option.id,
		label: option.label,
		value: option.label,
	};
};

export const getAgentPromptKey = prompt => prompt?.id || getAgentPromptData(prompt).id || 'prompt';

export const getAgentPromptSelection = prompt => {
	const data = getAgentPromptData(prompt);
	if (Array.isArray(prompt?.selectedIds)) return prompt.selectedIds;
	if (prompt?.selectedId) return [prompt.selectedId];
	if (Array.isArray(data.selectedIds)) return data.selectedIds;
	if (data.selectedId) return [data.selectedId];
	return [];
};

export const formatAgentPromptAnswerText = ({ feedback, options, prompt, selectedIds, skipped }) => {
	const title = getAgentPromptData(prompt).title || 'Planner question';
	if (skipped) return `Skipped: ${title}`;

	const selected = options.filter(option => selectedIds.includes(option.id));
	const labels = selected.map(option => option.value || option.label).filter(Boolean);
	const values = feedback ? [feedback] : labels;
	if (!values.length) return title;
	if (isImplementPlanPrompt(prompt) && selected[0]?.id === 'yes') return 'Yes, implement this plan';
	if (isImplementPlanPrompt(prompt) && selected[0]?.id === 'no') {
		return feedback ? `No, revise the plan: ${feedback}` : 'No, and tell Codex what to do differently';
	}
	return values.join('\n');
};

export const formatAgentPromptAnswers = prompts => {
	return prompts.reduce((answers, item) => {
		const key = getAgentPromptKey(item);
		answers[key] = {
			selectedIds: [],
			feedback: '',
			skipped: false,
			custom: false,
		};
		return answers;
	}, {});
};

export const isAgentPromptTypingTarget = target => {
	const tagName = target?.tagName;
	return tagName === 'INPUT' || tagName === 'TEXTAREA' || target?.isContentEditable;
};

const formatAgentStreamUrl = (baseDomain, url) => {
	if (!url) return url;
	if (/^https?:\/\//.test(url)) return url;
	if (!baseDomain) return url;
	return `${baseDomain.replace(/\/$/, '')}/${url.replace(/^\//, '')}`;
};

const parseAgentStreamErrorText = text => {
	if (!text) return '';
	try {
		const parsed = JSON.parse(text);
		return parsed?.error || parsed?.message || text;
	} catch (e) {
		return text;
	}
};

const readAgentStreamError = async response => {
	try {
		const text = await response.text();
		return parseAgentStreamErrorText(text);
	} catch (e) {
		return '';
	}
};

const parseAgentStreamBlock = block => {
	if (!block || block.startsWith(':')) return [];

	const data = block
		.split('\n')
		.filter(line => line.startsWith('data:'))
		.map(line => line.replace(/^data:\s?/, ''))
		.join('\n');
	if (!data) return [];

	const parsed = JSON.parse(data);
	return Array.isArray(parsed) ? parsed : [parsed];
};

export const createAgentErrorStream = (error, onError = null) => {
	return new ReadableStream({
		start(controller) {
			const errorText = normalizeAgentErrorText(error);
			onError?.({ title: 'Agent Error', message: errorText });
			controller.enqueue({ type: 'error', errorText });
			controller.close();
		},
	});
};

export const createConversationMessageStream = ({
	baseDomain,
	url,
	headers,
	abortSignal,
	messageId,
	messageMetadata = null,
	onComplete,
	onChunk = null,
	onError = null,
}) => {
	return new ReadableStream({
		async start(controller) {
			const streamUrl = formatAgentStreamUrl(baseDomain, url);
			const method = 'GET';
			const startedAt = new Date();
			const decoder = new TextDecoder();
			let buffer = '';
			let text = '';
			let started = false;
			let closed = false;
			const textPartValues = new Map();
			const reasoningPartValues = new Map();

			const startMessage = (chunk = null) => {
				if (closed) return;
				if (started) return;
				started = true;
				controller.enqueue(chunk || { type: 'start', messageId, messageMetadata });
			};

			const finish = async (chunk = null) => {
				if (closed) return;
				startMessage();
				closed = true;
				controller.enqueue(chunk || { type: 'finish', finishReason: 'stop' });
				await onComplete?.({ text });
				controller.close();
			};

			const fail = error => {
				if (closed) return;
				startMessage();
				closed = true;
				const errorText = error?.errorText || normalizeAgentErrorText(error);
				logger.error('agentStreamError: ', {
					method,
					url: streamUrl,
					status: error?.status,
					error: errorText,
				});
				onError?.({ title: 'Agent Stream Error', message: errorText });
				const errorChunk = { type: 'error', errorText, messageId };
				onChunk?.(errorChunk);
				controller.enqueue(errorChunk);
				controller.close();
			};

			const handleChunk = async chunk => {
				let chunks = [];

				try {
					chunks = normalizeAgentStreamChunk(chunk, messageId, textPartValues, reasoningPartValues);
				} catch (error) {
					return fail({ errorText: normalizeAgentErrorText(error) });
				}

				const type = getAgentStreamChunkType(chunk);
				logger.debug('agentStreamChunk: ', { method, url: streamUrl, chunkType: type });

				for (const nextChunk of chunks) {
					if (nextChunk.type === 'error') return fail(nextChunk);
					if (nextChunk.type === 'start') {
						onChunk?.({ ...nextChunk, messageId });
						return startMessage({
							...nextChunk,
							messageId,
							messageMetadata: nextChunk.messageMetadata || messageMetadata,
						});
					}
					startMessage();
					const messageChunk = { ...nextChunk, messageId: nextChunk.messageId || messageId };
					onChunk?.(messageChunk);
					if (messageChunk.type === 'text-delta') text = `${text}${messageChunk.delta || ''}`;
					if (messageChunk.type === 'finish') return await finish({ ...messageChunk, type: 'finish' });
					if (nextChunk.type === 'abort') {
						closed = true;
						controller.enqueue(messageChunk);
						await onComplete?.({ text });
						controller.close();
						return;
					}
					controller.enqueue(messageChunk);
				}
			};

			const handleBlock = async block => {
				const chunks = parseAgentStreamBlock(block);
				for (const chunk of chunks) await handleChunk(chunk);
			};

			try {
				logger.info('agentStreamRequest: ', { method, url: streamUrl });
				const response = await fetch(streamUrl, { headers, signal: abortSignal });
				logger.info(`agentStreamResponse (${timeElapsed(startedAt)}): `, {
					method,
					url: streamUrl,
					status: response.status,
					ok: response.ok,
				});
				if (!response.ok || !response.body) {
					const errorText = await readAgentStreamError(response);
					const error = new Error(errorText || 'The agent stream failed to connect.');
					(error as any).status = response.status;
					throw error;
				}

				const reader = response.body.getReader();
				while (true) {
					const { done, value } = await reader.read();
					if (done) break;
					buffer += decoder.decode(value, { stream: true });
					const blocks = buffer.split('\n\n');
					buffer = blocks.pop() || '';
					for (const block of blocks) await handleBlock(block);
					if (closed) {
						await reader.cancel();
						break;
					}
				}
				if (!closed) await finish();
			} catch (error) {
				if (abortSignal?.aborted) return fail({ message: 'The agent request was cancelled.' });
				fail(error);
			}
		},
	});
};
